import {Injectable} from '@angular/core';
import {deepEqual, isNonNull, TranslationService} from '@emma-labs/shared/angular';
import {
  ArchivedProposal,
  calculateAgeTruncated,
  CategoryCode,
  Coverage,
  GeneralError,
  getLanguageFromCode,
  getPresetRevisions,
  getTotalCoverage,
  QuestionId,
  QuestionnaireStatus,
  TranslatedUiQuestion,
} from '@emma-labs/shared/typescript';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  ArchivedProposalPageObject,
  ExtraQuestionnaire,
} from '../../entities/archived-proposal-object/archived-proposal-page-object';
import {RevisionAnswers} from '../../entities/archived-proposal-object/revision-answers';
import {SaleReport as SaleReportFrontendified} from '../../entities/page-shared-object/sale-report';
import {HealthQuestionnaireService} from '../health-questionnaire.service';
import {combineExtraQuestionnaires} from '../orchestrator-utils';
import {SaleReportService} from '../sale/sale-report.service';
import {ArchivedProposalService} from './archived-proposal.service';

@Injectable({
  providedIn: 'root',
})
export class ArchivedProposalPageOrchestrator {
  private readonly _store$: BehaviorSubject<ArchivedProposalPageObject | undefined>;
  private readonly store$: Observable<ArchivedProposalPageObject>;

  constructor(
    private readonly archivedProposalService: ArchivedProposalService,
    private readonly saleReportService: SaleReportService,
    private readonly healthQuestionnaireService: HealthQuestionnaireService,
    private readonly translate: TranslationService,
  ) {
    this._store$ = new BehaviorSubject<ArchivedProposalPageObject | undefined>(undefined);
    this.store$ = this._store$.pipe(filter(isNonNull), distinctUntilChanged(deepEqual));
  }

  get(archivedProposalId: string): Observable<ArchivedProposalPageObject> {
    const currentValue = this._store$.getValue();

    if (!currentValue || currentValue.archivedProposalId !== archivedProposalId) {
      this.clear();
      this.fetch(archivedProposalId);
    }

    return this.store$;
  }

  clear() {
    this._store$.next(undefined);
  }

  answerRevisions(revisionsAnswers: RevisionAnswers[]): Observable<void> {
    return this.archivedProposalService
      .answerHealthAnswersRevisions(this.getArchivedProposalId(), revisionsAnswers)
      .pipe(
        switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
        tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
        map(() => undefined),
      );
  }

  uploadHealthAnswersRevisionDocuments(healthAnswersRevisionId: string, documents: File[]): Observable<void> {
    return this.archivedProposalService
      .uploadHealthAnswersRevisionDocuments(this.getArchivedProposalId(), healthAnswersRevisionId, documents)
      .pipe(
        switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
        tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
        map(() => undefined),
      );
  }

  removeHealthAnswersRevisionDocument(
    healthAnswersRevisionId: string,
    documentName: string,
  ): Observable<void> {
    return this.archivedProposalService
      .removeHealthAnswersRevisionDocuments(
        this.getArchivedProposalId(),
        healthAnswersRevisionId,
        documentName,
      )
      .pipe(
        switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
        tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
        map(() => undefined),
      );
  }

  downloadDocument(healthAnswersRevisionId: string, documentName: string): Observable<Blob> {
    return this.archivedProposalService.getHealthAnswersRevisionDocument(
      this.getArchivedProposalId(),
      healthAnswersRevisionId,
      documentName,
    );
  }

  completeExtraQuestionnaires(ids: string[]): Observable<void> {
    return this.archivedProposalService
      .completeHealthAnswersRevisions(this.getArchivedProposalId(), ids)
      .pipe(
        switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
        tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
        map(() => undefined),
      );
  }

  sendModificationNotice(): Observable<void> {
    return this.archivedProposalService.get(this.getArchivedProposalId()).pipe(
      switchMap((archivedProposal) =>
        this.saleReportService.sendModificationNotice(archivedProposal.saleReport.id).pipe(
          map((saleReport) =>
            // Must set sale report to the archived proposal since saleReport.archivedProposal.saleReport is undefined
            ({
              ...archivedProposal,
              saleReport,
            }),
          ),
        ),
      ),
      switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
      tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
      map(() => undefined),
    );
  }

  updateProtection(coverage: Coverage): Observable<void> {
    return this.archivedProposalService.get(this.getArchivedProposalId()).pipe(
      switchMap((archivedProposal) =>
        this.saleReportService.updateProtection(archivedProposal.saleReport.id, coverage).pipe(
          map((saleReport) =>
            // Must set sale report to the archived proposal since saleReport.archivedProposal.saleReport is undefined
            ({
              ...archivedProposal,
              saleReport,
            }),
          ),
        ),
      ),
      switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
      tap((archivedProposalObject) => this._store$.next(archivedProposalObject)),
      map(() => undefined),
    );
  }

  private getArchivedProposalPageObject(
    archivedProposal: ArchivedProposal,
  ): Observable<ArchivedProposalPageObject> {
    return forkJoin([
      of(archivedProposal),
      this.healthQuestionnaireService.getQuestions(
        archivedProposal.providerId,
        archivedProposal.providerVersion.healthQuestionnaireVersion,
        archivedProposal.healthAnswers,
      ),
      archivedProposal.healthAnswersRevisions && archivedProposal.healthAnswersRevisions.length > 0
        ? this.healthQuestionnaireService.getExtraQuestionnaires(
            archivedProposal.providerId,
            archivedProposal.providerVersion.healthQuestionnaireVersion,
            getPresetRevisions(archivedProposal.healthAnswersRevisions).flatMap((revision) => {
              return revision.questionnaires.map((q) => ({
                ...q,
                healthAnswers: revision.answers ?? {},
              }));
            }),
          )
        : of(undefined),
    ]).pipe(
      map(([archivedProposal, healthQuestionnaire, revisionsExtraQuestionnaires]) => {
        if (!healthQuestionnaire.categoryCode) {
          throw new Error('Missing health questionnaire category code');
        }

        const extraQuestionnaires = combineExtraQuestionnaires(
          this.translate.currentLanguageIsFr(),
          archivedProposal,
          healthQuestionnaire.extraQuestionnaires,
          revisionsExtraQuestionnaires,
        );

        return this.buildArchivedProposalPageObject(archivedProposal, {
          ...healthQuestionnaire,
          categoryCode: healthQuestionnaire.categoryCode,
          extraQuestionnaires,
        });
      }),
    );
  }

  private getArchivedProposalId(): string {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot get an archivedProposalId that was never fetched.');
    }

    return storeValue.archivedProposalId;
  }

  private buildArchivedProposalPageObject(
    archivedProposal: ArchivedProposal,
    healthQuestionnaire: {
      questions: TranslatedUiQuestion[];
      status: QuestionnaireStatus;
      categoryCode: CategoryCode;
      extraQuestionnaires: ExtraQuestionnaire[];
    },
  ): ArchivedProposalPageObject {
    if (!archivedProposal.extra || !archivedProposal.extra.idDocument || !archivedProposal.saleReport) {
      throw new Error('Missing required data for archived proposal page object');
    }

    const saleReport = archivedProposal.saleReport;
    const fna = saleReport.fna;
    const insuree = archivedProposal.insuree;
    const healthAnswers = archivedProposal.healthAnswers;

    const primaryAgent = archivedProposal.agents.find((agent) => agent.isPrimary);
    const primaryAgentEmail = primaryAgent?.email ?? 'Self-Serve';

    const srFrontendified: SaleReportFrontendified = {
      id: saleReport.id,
      insurer: saleReport.insurer,
      insurances: saleReport.protection.personInsurances.map((i) => ({
        annualPremium: i.annualPrice,
        coverage: i.coverage,
        duration: i.duration,
        insuranceType: i.type,
      })),
      fullName: `${archivedProposal.insuree.firstName} ${archivedProposal.insuree.lastName}`,
      policyNumber: saleReport.policyNumber || '--',
      status: saleReport.status,
      type: saleReport.type,
      agent: primaryAgentEmail,
      hasReplacementNotice: archivedProposal.hasPolicyReplacementNotice,
      canDownloadContract: saleReport.contractReceived,
      protection: saleReport.protection,
    };

    const pageObject: ArchivedProposalPageObject = {
      archivedProposalId: archivedProposal.id,
      saleReport: srFrontendified,
      client: {
        firstName: insuree.firstName,
        middleName: insuree.middleName ?? '--',
        lastName: insuree.lastName,
        province: insuree.province,
        phone: insuree.phone,
        email: insuree.email,
        preferredLanguage: getLanguageFromCode(insuree.languageCode),
        birthday: new Date(healthAnswers.birthday),
        sex: healthAnswers.sex,
        proposalSubmittedAt: archivedProposal.submittedAt,
      },
      fna: {...fna, jobCategoryId: archivedProposal.jobCategoryId},
      healthQuestionnaire: {
        status: healthQuestionnaire.status,
        categoryCode: healthQuestionnaire.categoryCode,
        questions: healthQuestionnaire.questions.filter(
          (q) => q.id !== QuestionId.SEX && q.id !== QuestionId.BIRTHDAY,
        ),
      },
      extraQuestionnaireInfo: {
        extraQuestionnaires: healthQuestionnaire.extraQuestionnaires,
        canModifyIsIncludedInContract: false,
        canAnswerQuestions: true,
      },
      healthAnswers: {
        ...archivedProposal.healthAnswers,
        age: calculateAgeTruncated(healthAnswers.birthday, archivedProposal.submittedAt),
      },
      protection: archivedProposal.protection,
      modificationNoticeInfo: saleReport.modificationNotice
        ? {
            modificationNotice: saleReport.modificationNotice,
            languageCode: archivedProposal.insuree.languageCode,
            hasModificationDecision: !!saleReport.modificationDecision,
          }
        : undefined,
      refusalNoticeInfo: saleReport.refusalNotice
        ? {
            refusalNotice: saleReport.refusalNotice,
            languageCode: archivedProposal.insuree.languageCode,
          }
        : undefined,
      totalCoverage: getTotalCoverage(archivedProposal.protection.personInsurances),
      beneficiaries: archivedProposal.beneficiaries
        ? archivedProposal.beneficiaries.map((b) => ({
            ...b,
            birthday: new Date(b.birthday),
            trustee: b.trustee
              ? {
                  ...b.trustee,
                  birthday: new Date(b.trustee.birthday),
                }
              : undefined,
          }))
        : [],
      address: {
        address: insuree.address,
        city: insuree.city,
        postalCode: insuree.postalCode,
        unitNumber: insuree.unitNumber,
      },
      idDocument: archivedProposal.extra.idDocument,
      ratesVersion: archivedProposal.providerVersion.ratesVersion,
    };

    if (!primaryAgent) {
      throw new GeneralError('Primary agent is undefined', {
        tags: ['ArchivedProposalPageOrchestrator', 'buildArchivedProposalPageObject'],
      });
    }

    if (archivedProposal.extra.clientAgentAffiliationTime) {
      pageObject.disclosures = {
        primaryAgentFullName: `${primaryAgent.firstName} ${primaryAgent.lastName}`,
        clientAgentAffiliationTime: archivedProposal.extra.clientAgentAffiliationTime,
        isAccepted: true,
      };
    }
    return pageObject;
  }

  private fetch(archivedProposalId: string): void {
    this.archivedProposalService
      .get(archivedProposalId)
      .pipe(
        first(),
        switchMap((archivedProposal) => this.getArchivedProposalPageObject(archivedProposal)),
      )
      .subscribe((archivedProposalPageObject) => this._store$.next(archivedProposalPageObject));
  }
}
